
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Key, Wand2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface APISettings {
  hugging_face_token?: string;
  openai_api_key?: string;
}

const APISettingsTab = () => {
  const queryClient = useQueryClient();

  // Fetch current API settings
  const { data: apiSettings, isLoading } = useQuery({
    queryKey: ['api-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_settings')
        .select('key, value')
        .in('key', ['hugging_face_token', 'openai_api_key']);
      
      if (error) throw error;
      
      const settingsObj = (data || []).reduce((acc: Record<string, any>, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {});
      
      return settingsObj as APISettings;
    },
  });

  // Update API settings mutation
  const updateAPISettingsMutation = useMutation({
    mutationFn: async (settings: APISettings) => {
      const updatePromises = Object.entries(settings).map(async ([key, value]) => {
        if (value && value.trim()) {
          const { error } = await supabase
            .from('website_settings')
            .upsert({ 
              key, 
              value: value.trim(),
              description: `API setting for ${key}`
            }, {
              onConflict: 'key'
            });
          
          if (error) throw error;
        }
      });
      
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-settings'] });
      toast.success('API settings updated successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to update API settings');
      console.error('API settings update error:', error);
    },
  });

  const [huggingFaceToken, setHuggingFaceToken] = React.useState('');
  const [openaiKey, setOpenaiKey] = React.useState('');

  React.useEffect(() => {
    if (apiSettings) {
      setHuggingFaceToken(apiSettings.hugging_face_token || '');
      setOpenaiKey(apiSettings.openai_api_key || '');
    }
  }, [apiSettings]);

  const handleSave = () => {
    updateAPISettingsMutation.mutate({
      hugging_face_token: huggingFaceToken,
      openai_api_key: openaiKey,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              These API keys are used for AI content generation features. Keep them secure and never share them publicly.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="huggingface">Hugging Face API Token</Label>
              <Input
                id="huggingface"
                type="password"
                placeholder="hf_xxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={huggingFaceToken}
                onChange={(e) => setHuggingFaceToken(e.target.value)}
              />
              <p className="text-sm text-gray-600">
                Used for AI content generation in games and blog posts. 
                <a 
                  href="https://huggingface.co/settings/tokens" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline ml-1"
                >
                  Get your token here
                </a>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="openai">OpenAI API Key (Optional)</Label>
              <Input
                id="openai"
                type="password"
                placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
              />
              <p className="text-sm text-gray-600">
                Optional: Used for enhanced AI features if available.
                <a 
                  href="https://platform.openai.com/api-keys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline ml-1"
                >
                  Get your key here
                </a>
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={handleSave}
              disabled={updateAPISettingsMutation.isPending}
              className="flex items-center gap-2"
            >
              <Wand2 className="w-4 h-4" />
              {updateAPISettingsMutation.isPending ? 'Saving...' : 'Save API Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI Content Generation Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>Hugging Face (Game Descriptions, Tags, Blog Posts)</span>
              <span className={`px-2 py-1 rounded text-sm ${
                huggingFaceToken ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {huggingFaceToken ? 'Configured' : 'Not Configured'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>OpenAI (Enhanced Features)</span>
              <span className={`px-2 py-1 rounded text-sm ${
                openaiKey ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {openaiKey ? 'Configured' : 'Optional'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APISettingsTab;
